"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { ArrowRight, BarChart2, TrendingUp, Shield, Zap } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"

interface HeroSectionProps {
  onRequestAccess: () => void
}

interface HeroContent {
  badge?: {
    text: string
    icon: JSX.Element
  }
  title: {
    prefix: string
    highlight: string
    suffix: string
  }
  subheadline?: string
  description: string
  primaryButton: {
    text: string
    icon: JSX.Element
  }
  secondaryButton: {
    text: string
    link: string
  }
}

const contents: HeroContent[] = [
  {
    badge: {
      text: "Quant Trading Intelligence",
      icon: <Zap className="w-4 h-4 mr-1" />,
    },
    title: {
      prefix: "",
      highlight: "AI-Powered Trading Intelligence",
      suffix: "",
    },
    subheadline: "Institutional-grade strategies, simplified for modern investors",
    description:
      "Stewie Trader combines advanced machine learning with financial expertise to deliver market insights, custom strategy design, and intelligent trade execution—all in one platform.",
    primaryButton: {
      text: "Start Trading Smarter",
      icon: <ArrowRight className="ml-2 h-5 w-5" />,
    },
    secondaryButton: {
      text: "Learn More",
      link: "/docs",
    },
  },
  {
    badge: {
      text: "New Blog Post",
      icon: <Zap className="w-4 h-4 mr-1" />,
    },
    title: {
      prefix: "Predicting Market ",
      highlight: "Storms",
      suffix: ": How Transformers Forecast Volatility",
    },
    description:
      "Discover how our advanced Transformer models are revolutionizing market volatility forecasting. These AI models identify subtle patterns in financial data to predict market turbulence before it happens, giving you a crucial edge in uncertain times.",
    primaryButton: {
      text: "Read the Full Article",
      icon: <ArrowRight className="ml-2 h-5 w-5" />,
    },
    secondaryButton: {
      text: "Learn More",
      link: "https://www.stewietrader.com/blog/predicting-market-storms-transformer-models",
    },
  },
]

export const HeroSection = ({ onRequestAccess }: HeroSectionProps) => {
  const [contentIndex, setContentIndex] = useState(0)
  const navigate = useNavigate()
  const [animationComplete, setAnimationComplete] = useState(true)
  const chartCanvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationComplete(false)
      setTimeout(() => {
        setContentIndex((current) => (current + 1) % contents.length)
        setAnimationComplete(true)
      }, 500)
    }, 11250)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!chartCanvasRef.current) return

    const canvas = chartCanvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Set canvas dimensions
    const setCanvasDimensions = () => {
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.scale(dpr, dpr)
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
    }

    setCanvasDimensions()
    window.addEventListener("resize", setCanvasDimensions)

    // Chart data
    const data = Array.from({ length: 100 }, () => Math.random() * 50 + 50)
    const secondaryData = Array.from({ length: 100 }, () => Math.random() * 30 + 30)

    // Animation variables
    let animationFrame: number
    let currentPoint = 0

    // Draw function
    const draw = () => {
      if (!ctx) return

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw grid
      ctx.strokeStyle = "rgba(148, 163, 184, 0.1)"
      ctx.lineWidth = 1

      // Horizontal grid lines
      for (let i = 0; i < 5; i++) {
        const y = ((canvas.height / 5) * i) / (window.devicePixelRatio || 1)
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width / (window.devicePixelRatio || 1), y)
        ctx.stroke()
      }

      // Vertical grid lines
      for (let i = 0; i < 10; i++) {
        const x = ((canvas.width / 10) * i) / (window.devicePixelRatio || 1)
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height / (window.devicePixelRatio || 1))
        ctx.stroke()
      }

      // Draw secondary line
      ctx.strokeStyle = "rgba(99, 102, 241, 0.4)"
      ctx.lineWidth = 2
      ctx.beginPath()

      const width = canvas.width / (window.devicePixelRatio || 1)
      const height = canvas.height / (window.devicePixelRatio || 1)

      for (let i = 0; i <= currentPoint; i++) {
        const x = (width / 100) * i
        const y = height - (height / 100) * secondaryData[i]
        if (i === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      }
      ctx.stroke()

      // Draw main line
      const gradient = ctx.createLinearGradient(0, 0, 0, height)
      gradient.addColorStop(0, "rgba(59, 130, 246, 1)")
      gradient.addColorStop(1, "rgba(139, 92, 246, 1)")

      ctx.strokeStyle = gradient
      ctx.lineWidth = 3
      ctx.beginPath()

      for (let i = 0; i <= currentPoint; i++) {
        const x = (width / 100) * i
        const y = height - (height / 100) * data[i]
        if (i === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      }
      ctx.stroke()

      // Draw data points
      if (currentPoint > 0) {
        const x = (width / 100) * currentPoint
        const y = height - (height / 100) * data[currentPoint]

        // Glow effect
        ctx.shadowColor = "rgba(59, 130, 246, 0.7)"
        ctx.shadowBlur = 10

        // Draw point
        ctx.fillStyle = "#ffffff"
        ctx.beginPath()
        ctx.arc(x, y, 4, 0, Math.PI * 2)
        ctx.fill()

        // Reset shadow
        ctx.shadowBlur = 0
      }

      // Animate
      if (currentPoint < 99) {
        currentPoint++
        animationFrame = requestAnimationFrame(draw)
      }
    }

    // Start animation
    draw()

    return () => {
      cancelAnimationFrame(animationFrame)
      window.removeEventListener("resize", setCanvasDimensions)
    }
  }, [contentIndex])

  const currentContent = contents[contentIndex]

  const handlePrimaryButtonClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (contentIndex === 1) {
      window.location.href = "https://www.stewietrader.com/blog/predicting-market-storms-transformer-models"
    } else {
      onRequestAccess()
    }
  }

  const handleDotClick = (index: number) => {
    if (index === contentIndex) return
    setAnimationComplete(false)
    setTimeout(() => {
      setContentIndex(index)
      setAnimationComplete(true)
    }, 500)
  }

  const handleSecondaryButtonClick = (e: React.MouseEvent, link: string) => {
    e.preventDefault()
    if (link.startsWith("http")) {
      window.open(link, "_blank")
    } else {
      navigate(link)
    }
  }

  return (
    <div className="relative w-full overflow-hidden pt-10 md:pt-16 mb-12">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: animationComplete ? 1 : 0, y: animationComplete ? 0 : 20 }}
            transition={{ duration: 0.5 }}
            className="text-left"
          >
            {currentContent.badge && (
              <div className="inline-flex items-center mb-6 px-4 py-2 rounded-full bg-gradient-to-r from-blue-600/10 to-violet-600/10 border border-blue-500/20">
                <span className="text-blue-400 font-semibold flex items-center">
                  {currentContent.badge.icon} {currentContent.badge.text}
                </span>
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {currentContent.title.prefix}
              <span className="bg-gradient-to-r from-blue-400 to-violet-500 text-transparent bg-clip-text">
                {currentContent.title.highlight}
              </span>
              {currentContent.title.suffix}
            </h1>

            {currentContent.subheadline && (
              <p className="text-xl sm:text-2xl font-medium text-blue-300 mb-4 leading-relaxed">{currentContent.subheadline}</p>
            )}

            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">{currentContent.description}</p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-lg h-14 px-8 rounded-md w-full sm:w-auto text-white"
                onClick={handlePrimaryButtonClick}
              >
                {currentContent.primaryButton.text}
                {currentContent.primaryButton.icon}
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="text-lg h-14 px-8 rounded-md border-gray-700 hover:bg-gray-800 bg-gray-900/80 text-white font-medium w-full sm:w-auto"
                onClick={(e) => handleSecondaryButtonClick(e, currentContent.secondaryButton.link)}
              >
                {currentContent.secondaryButton.text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="flex items-center gap-3 mt-8">
              {contents.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`h-3 rounded-full transition-all duration-500 ${
                    index === contentIndex ? "bg-blue-500 w-6" : "bg-gray-500/20 w-3"
                  }`}
                  aria-label={`Switch to content ${index + 1}`}
                  style={{ minHeight: "12px", minWidth: index === contentIndex ? "24px" : "12px" }}
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative h-[400px] rounded-xl overflow-hidden border border-gray-800 bg-gray-900/50 backdrop-blur-sm"
          >
            <canvas ref={chartCanvasRef} className="w-full h-full" />

            {/* Floating data indicators */}
            <div className="absolute top-4 left-4 p-3 rounded-lg bg-gray-800/80 border border-gray-700 backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-400" />
                <span className="text-white font-medium">ALGO SIGNAL</span>
              </div>
              <div className="text-2xl font-bold text-green-400 mt-1">+24.7%</div>
            </div>

            <div className="absolute bottom-4 right-4 p-3 rounded-lg bg-gray-800/80 border border-gray-700 backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-400" />
                <span className="text-white font-medium">RISK SCORE</span>
              </div>
              <div className="mt-1 w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full w-1/5"></div>
              </div>
              <div className="text-xs text-blue-400 mt-1 text-right">LOW</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Background elements */}
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-blob"></div>
      <div className="absolute top-1/4 right-0 w-72 h-72 bg-violet-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-blob animation-delay-4000"></div>
    </div>
  )
}
